import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { UserPlus, Trash2, Shield, User } from "lucide-react";

export default function Team() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const isAdmin = user?.role === "admin";
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "agent" });

  const { data: team = [] } = useQuery({
    queryKey: ["team"],
    queryFn: () => api.get("/team").then((r) => r.data),
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/team", form);
      toast.success("Rep added");
      setForm({ name: "", email: "", password: "", role: "agent" });
      qc.invalidateQueries({ queryKey: ["team"] });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/team/${id}`);
      toast.success("Removed");
      qc.invalidateQueries({ queryKey: ["team"] });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="team-page">
      <div className="mb-6">
        <h1 className="font-display text-3xl font-extrabold tracking-tight">Team</h1>
        <p className="text-muted-foreground mt-1">Add reps and split the pipeline across your sales team.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {isAdmin && (
          <form onSubmit={create} className="rounded-2xl border border-border bg-card p-6 space-y-4 h-fit" data-testid="add-rep-form">
            <h3 className="font-display font-semibold flex items-center gap-2"><UserPlus className="h-4 w-4 text-primary" /> Add a rep</h3>
            <div className="space-y-2"><Label>Name</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="rep-name-input" /></div>
            <div className="space-y-2"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required data-testid="rep-email-input" /></div>
            <div className="space-y-2"><Label>Temporary password</Label><Input type="text" value={form.password} onChange={(e) => set("password", e.target.value)} required data-testid="rep-password-input" /></div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => set("role", v)}>
                <SelectTrigger data-testid="rep-role-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-popover border-border">
                  <SelectItem value="agent">Agent</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" className="w-full bg-primary text-primary-foreground hover:bg-primary/90" data-testid="create-rep-button">Add rep</Button>
          </form>
        )}

        <div className={isAdmin ? "lg:col-span-2 space-y-3" : "lg:col-span-3 space-y-3"}>
          {team.map((m) => (
            <div key={m.id} className="rounded-xl border border-border bg-card p-4 flex items-center justify-between" data-testid={`team-member-${m.id}`}>
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary text-sm font-semibold uppercase">
                  {(m.name || m.email).slice(0, 2)}
                </div>
                <div>
                  <p className="font-medium">{m.name}</p>
                  <p className="text-xs text-muted-foreground">{m.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full ${m.role === "admin" ? "bg-primary/10 text-primary" : "bg-secondary text-muted-foreground"}`}>
                  {m.role === "admin" ? <Shield className="h-3 w-3" /> : <User className="h-3 w-3" />} {m.role}
                </span>
                {isAdmin && m.id !== user?.id && (
                  <Button size="icon" variant="ghost" onClick={() => remove(m.id)} data-testid={`delete-member-${m.id}`}>
                    <Trash2 className="h-4 w-4 text-rose-400" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
